import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createElement,
} from 'react';
import { StyleSheet, type ViewStyle } from 'react-native';

interface WebViewProps {
  title?: string;
  source: {
    uri?: string;
    html?: string;
    method?: string;
    body?: string;
    headers?: Record<string, string>;
  };
  width?: number;
  height?: number;
  onLoad?: () => void;
  onMessage?: (params: { nativeEvent: { data?: string } }) => void;
  scrollEnabled?: boolean;
  injectedJavaScript?: string;
  style?: ViewStyle;
  cookies?: Array<{
    name: string;
    value?: string | null;
    domain?: string;
    path?: string;
  }>;
}

export const WebView: React.FC<WebViewProps> = ({
  title,
  source,
  onLoad,
  onMessage,
  scrollEnabled = true,
  injectedJavaScript,
  style,
  width,
  height,
  cookies,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Cookie 설정을 위한 JavaScript 생성
  const getCookieInjectionScript = useCallback(() => {
    if (!cookies || cookies.length === 0) return '';

    const cookieStatements = cookies
      .map(cookie => {
        const { name, value, domain, path = '/' } = cookie;
        let cookieString = `${name}=${value}; path=${path}`;
        if (domain) {
          cookieString += `; domain=${domain}`;
        }
        return `document.cookie = "${cookieString}";`;
      })
      .join('\n');

    return `
      (function() {
        ${cookieStatements}
      })();
    `;
  }, [cookies]);

  const handleInjectedJavaScript = useCallback(
    (html: string | undefined) => {
      const cookieScript = getCookieInjectionScript();
      const combinedScript = [cookieScript, injectedJavaScript]
        .filter(Boolean)
        .join('\n');
      if (!html) return html;

      if (combinedScript) {
        return html.replace(
          '</body>',
          `<script>${combinedScript}</script></body>`,
        );
      }
      return html;
    },
    [injectedJavaScript, getCookieInjectionScript],
  );

  const handleSourceInIFrame = useCallback(() => {
    if (!source.uri) return;

    // Cookie를 헤더에 추가
    const fetchHeaders: Record<string, string> = {};
    if (cookies && cookies.length > 0) {
      const cookieHeader = cookies.map(c => `${c.name}=${c.value}`).join('; ');
      fetchHeaders['Cookie'] = cookieHeader;
    }

    fetch(source.uri, {
      method: 'GET',
      headers: fetchHeaders,
      credentials: 'include', // 브라우저의 쿠키를 자동으로 포함
    })
      .then(response => response.text())
      .then(html => setHtml(`<base href="${source.uri}" />${html}`));
  }, [cookies, source.uri]);

  const handleMessage = useCallback(
    (params: { nativeEvent: { data?: string } }) => {
      if (onMessage) {
        onMessage(params);
      }
    },
    [onMessage],
  );

  useEffect(() => {
    handleSourceInIFrame();
  }, [handleSourceInIFrame]);

  // message 이벤트 리스너
  useEffect(() => {
    if (onMessage) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.addEventListener('message', handleMessage, true);
      return () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.removeEventListener('message', handleMessage, true);
      };
    }
  }, [onMessage, handleMessage]);

  return createElement('iframe', {
    title,
    ref: frameRef,
    // src: !source.method ? source.uri : undefined,
    srcDoc: handleInjectedJavaScript(html ?? source.html ?? undefined),
    width: width,
    height: height,
    style: StyleSheet.flatten([
      styles.iframe,
      scrollEnabled && styles.noScroll,
      style,
    ]),
    allowFullScreen: true,
    allowpaymentrequest: 'true',
    frameBorder: '0',
    seamless: true,
    onLoad,
  });
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iframe: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
  },
  noScroll: {
    overflow: 'hidden',
  },
});
